import { Instance, SnapshotOut, types } from 'mobx-state-tree';

import { Color, Cutoff, IColor, ICutoff } from '../../models/cutoff/Cutoff';
import {
  CutoffApi,
  TDeleteCutoffResult,
  TGetColorsResult,
  TGetCutoffListResult,
  TGetCutoffsResult,
  TPostCutoffResult,
  TUpdateCutoffResult,
} from '../../services';
import { ICommonCode } from '../common-code/CommonCode';
import { withEnvironment } from '../extensions/with-environment';
import { withRootStore } from '../extensions/with-root-store';
import { defaultPagination, withPagination } from '../extensions/withPagination';
import { withSetPropAction } from '../extensions/withSetPropAction';

/**
 * # CutoffStore
 *
 * CutoffStore을 설명하세요.
 */
export const CutoffStore = types
  .model('CutoffStore')
  // --------------------------------------------------------------------------
  .props({
    cutoffList: types.optional(types.array(Cutoff), []),
    colors: types.optional(types.array(Color), []),
  })
  .extend(withEnvironment)
  .extend(withRootStore)
  .extend(withPagination)
  .actions(withSetPropAction)
  // eslint-disable-line @typescript-eslint/no-unused-vars
  .views((self) => ({}))
  // --------------------------------------------------------------------------
  // MUTATEs - 모델 상태를 변경
  .actions((self) => ({
    /**
     * cutoffs을 교체
     *
     * @param `cutoffs` 새로운 모델의 배열
     */
    setCutoffList: (cutoffs: ICutoff[]) => {
      self.cutoffList.replace(cutoffs);
    },

    setColors: (colors: IColor[]) => {
      self.colors.replace(colors);
    },
  }))
  // --------------------------------------------------------------------------
  // REQUESTs - 서비스 요청 및 기타 인터페이스 요청
  .actions((self) => ({
    /**
     * 전체 목록을 Api를 통해 조회
     *
     * 조회한 결과로 Cutoffs를 교체한다. 실패시 에러 로그를 남긴다.
     */
    getCutoffList: async () => {
      try {
        const cutoffApi: CutoffApi = new CutoffApi(self.environment.api);
        const result: TGetCutoffListResult = await cutoffApi.getCutoffList(self.pagination);

        if (
          self.rootStore.responseStore.getResponseResult(result) &&
          self.rootStore.responseStore.responseInfo.resultCode === 'S'
        ) {
          if (result.cutoffList && result.pagination) {
            self.setCutoffList(result.cutoffList);
            self.setPagination(result.pagination);
          } else if (!result.pagination) {
            self.setPagination(defaultPagination);
          }
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    getCutoffAll: async () => {
      try {
        const cutoffApi: CutoffApi = new CutoffApi(self.environment.api);
        const result: TGetCutoffsResult = await cutoffApi.getCutoffAll();

        if (
          self.rootStore.responseStore.getResponseResult(result) &&
          self.rootStore.responseStore.responseInfo.resultCode === 'S'
        ) {
          if (result.cutoffs) {
            self.setCutoffList(result.cutoffs);
          }
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    postCutoff: async (data: any) => {
      try {
        const cutoffApi: CutoffApi = new CutoffApi(self.environment.api);
        const result: TPostCutoffResult = await cutoffApi.postCutoff(data);

        self.rootStore.responseStore.getResponseResult(result);
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    updateCutoff: async (data: any) => {
      try {
        const cutoffApi: CutoffApi = new CutoffApi(self.environment.api);
        const result: TUpdateCutoffResult = await cutoffApi.updateCutoff(data);

        self.rootStore.responseStore.getResponseResult(result);
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    deleteCutoff: async (cutoffSid: any) => {
      try {
        const cutoffApi: CutoffApi = new CutoffApi(self.environment.api);
        const result: TDeleteCutoffResult = await cutoffApi.deleteCutoff(cutoffSid);
        self.rootStore.responseStore.getResponseResult(result);
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    getColors: async () => {
      try {
        const cutoffApi: CutoffApi = new CutoffApi(self.environment.api);
        const result: TGetColorsResult = await cutoffApi.getColorList();
        if (
          self.rootStore.responseStore.getResponseResult(result) &&
          self.rootStore.responseStore.responseInfo.resultCode === 'S'
        ) {
          if (result.colorList) {
            self.setColors(result.colorList);
          }
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },
  }));

// --------------------------------------------------------------------------
type TCutoffStore = Instance<typeof CutoffStore>;
type TCutoffStoreSnapshot = SnapshotOut<typeof CutoffStore>;

export interface ICutoffStore extends TCutoffStore {}
export type TCutoffStoreKeys = keyof TCutoffStoreSnapshot & string;
export interface ICutoffStoreSnapshot extends TCutoffStoreSnapshot {}
export const createCutoffStore = () => types.optional(CutoffStore, {} as TCutoffStore);
